import dayjs from 'dayjs';
import { inject } from '@angular/core';
import {
  clearStore,
  endAuthStepHandler,
  initAuth,
  initAuthSuccess,
  logout,
  logoutSuccess,
  setAuthStep,
} from './auth.actions';
import { Actions, FunctionalEffect, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseAuthStatus, TwofaSetupStatus } from '@app/core/authentication/enums/auth-status.enum';
import { AuthStep } from '@app/core/authentication/enums/auth-step.enum';
import { Store } from '@ngrx/store';
import {
  selectAuthStep,
  selectAuthStepData,
  selectIsAuthInitialized,
} from '@app/core/authentication/store/auth/auth.selectors';
import { BaseErrorStatus } from '@app/api/enums/base-error-status.enum';
import { AuthApiService } from '@app/api/services/auth-api.service';
import { AppRoute } from '@app/app-routes.enum';
import { Router } from '@angular/router';
import { AuthRoute } from '@app/core/authentication/auth-routes.enum';
import { SessionMarkerService } from '@app/core/authentication/services/session-marker.service';
import { IAuthStepData } from '@app/core/authentication/interfaces/auth-step-data';
import { ITwofaSetupStepData } from '@app/core/authentication/interfaces/twofa-setup-step-data';
import { ILoginWithEncryptPassword } from '@app/api/interfaces/auth.interface';
import { ITwofaVerifyStepData } from '@app/core/authentication/interfaces/twofa-verify-step-data';
import { ISubmitResetPasswordStepData } from '@app/core/authentication/interfaces/submit-reset-password-step-data';
import { from, of } from 'rxjs';
import { ChartZoomHelpService } from '@app/core/services/charts/chart-zoom-help.service';
import { UserUiStateSharedApiService } from '@app/api/services/user-ui-state-shared-api.service';
import { selectMainSharedState } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';

export const initAuth$: FunctionalEffect = createEffect(
  (
    actions$: Actions = inject(Actions),
    store: Store = inject(Store),
    sessionMarkerService: SessionMarkerService = inject(SessionMarkerService)
  ) => {
    return actions$.pipe(
      ofType(initAuth),
      withLatestFrom(store.select(selectIsAuthInitialized)),
      filter(([, isAuthInitialized]) => !isAuthInitialized),
      map(() => {
        const isSessionExist = sessionMarkerService.isSessionActive();

        const step = isSessionExist ? AuthStep.Authenticated : AuthStep.Login;

        return initAuthSuccess({ step });
      })
    );
  },
  { functional: true }
);

export const doRequestSaveUserMeContext$: FunctionalEffect = createEffect(
  (actions$: Actions = inject(Actions), store: Store = inject(Store)) => {
    return actions$.pipe(
      ofType(endAuthStepHandler),
      withLatestFrom(store.select(selectAuthStep), store.select(selectAuthStepData)),
      map(([{ error, success, sharedAuthContext }, currentAuthStep, currentAuthStepData]) => {
        let step = currentAuthStep || AuthStep.Login;
        let stepData: IAuthStepData = currentAuthStepData || null;

        if (success) {
          switch (currentAuthStep) {
            case AuthStep.ResetPassword:
              // stay on the same step
              break;
            case AuthStep.SubmitResetPassword:
              step = AuthStep.Login;
              stepData = null;
              break;
            default:
              step = AuthStep.Authenticated;
              stepData = null;
              break;
          }
        } else {
          switch (error?.status) {
            case TwofaSetupStatus.TwofaSetup: {
              step = AuthStep.TwofaSetup;

              const newStepData: ITwofaSetupStepData = {
                credential: sharedAuthContext as ILoginWithEncryptPassword,
                totpAppLink: error.data || '',
              };
              stepData = newStepData;
              break;
            }
            case TwofaSetupStatus.TwofaRequired: {
              step = AuthStep.TwofaVerify;

              const newStepData: ITwofaVerifyStepData = { credential: sharedAuthContext as ILoginWithEncryptPassword };
              stepData = newStepData;
              break;
            }
            case BaseErrorStatus.BadRequest:
            case BaseErrorStatus.FailedTooManyCalls:
            case BaseErrorStatus.TooManyRequests:
            case BaseErrorStatus.FailedUnexpected:
            case BaseAuthStatus.FailedUserUnknown:
            case BaseAuthStatus.FailedUserDisabled:
            case BaseAuthStatus.FailedWrongPassword:
            case BaseAuthStatus.FailedResetPasswordTokenExpired:
            case TwofaSetupStatus.TwofaWrongCode:
              // stay on the same step
              break;
            default:
              // stay on the same step
              break;
          }
        }

        return setAuthStep({ step, stepData });
      })
    );
  },
  { functional: true }
);

export const logout$: FunctionalEffect = createEffect(
  (
    actions$: Actions = inject(Actions),
    store: Store = inject(Store),
    authApiService: AuthApiService = inject(AuthApiService),
    uiStateSharedApiService: UserUiStateSharedApiService = inject(UserUiStateSharedApiService)
  ) => {
    return actions$.pipe(
      ofType(logout),
      withLatestFrom(store.select(selectMainSharedState)),
      exhaustMap(([, currentSharedState]) =>
        uiStateSharedApiService.update({ ...currentSharedState, lastLogoutAt: dayjs().toISOString() }).pipe(
          catchError(() => of(null)),
          switchMap(() => authApiService.logout().pipe(catchError(() => of(null)))), // silence the logout API call
          map(() => logoutSuccess())
        )
      )
    );
  },
  { functional: true }
);

export const redirect$: FunctionalEffect = createEffect(
  (
    actions$: Actions = inject(Actions),
    router: Router = inject(Router),
    sessionMarkerService: SessionMarkerService = inject(SessionMarkerService),
    zoomHelpService: ChartZoomHelpService = inject(ChartZoomHelpService)
  ) => {
    return actions$.pipe(
      ofType(setAuthStep),
      map(action => {
        if (action.step === AuthStep.Authenticated) {
          sessionMarkerService.setSessionActive();
        } else {
          if (sessionMarkerService.isSessionActive()) {
            sessionMarkerService.deleteSessionState();
          }
          zoomHelpService.deleteZoomHelpDialogBlocked();
        }

        return action;
      }),
      filter(({ withoutRedirect }) => !withoutRedirect),
      switchMap(({ step, stepData, clearAllStore }) => {
        let redirectPromise: Promise<boolean> | null;

        switch (step) {
          case AuthStep.Authenticated: {
            redirectPromise = router.navigate([AppRoute.Base]);
            break;
          }
          case AuthStep.TwofaSetup:
            redirectPromise = router.navigate([AppRoute.Auth, AuthRoute.TwoFASetup]);
            break;
          case AuthStep.TwofaVerify:
            redirectPromise = router.navigate([AppRoute.Auth, AuthRoute.TwoFA]);
            break;
          case AuthStep.ResetPassword:
            redirectPromise = router.navigate([AppRoute.Auth, AuthRoute.ResetPassword]);
            break;
          case AuthStep.SubmitResetPassword:
            if (!stepData || !(stepData as ISubmitResetPasswordStepData).token) {
              redirectPromise = router.navigate([AppRoute.Auth, AuthRoute.ConfirmResetPassword], {
                queryParams: { token: (stepData as ISubmitResetPasswordStepData)?.token },
              });
            } else {
              redirectPromise = router.navigate([AppRoute.Auth, AuthRoute.Login]);
            }
            break;
          case AuthStep.Login:
          default:
            redirectPromise = router.navigate([AppRoute.Auth, AuthRoute.Login]);
            break;
        }

        return from(redirectPromise).pipe(
          filter(() => !!clearAllStore),
          map(() => clearStore())
        );
      })
    );
  },
  { functional: true, dispatch: true }
);

export const logoutSuccess$: FunctionalEffect = createEffect(
  (actions$: Actions = inject(Actions)) => {
    return actions$.pipe(
      ofType(logoutSuccess),
      map(() => setAuthStep({ step: AuthStep.Login, stepData: null, clearAllStore: true }))
    );
  },
  { functional: true }
);
